import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMAGES } from "@/lib/api";

const lines = ["ENGINEERING", "EXTRAORDINARY", "EXPERIENCES"];

const container = { animate: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } } };
const lineUp = {
  initial: { y: "110%" },
  animate: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[680px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Bhaaskar Productions concert stage" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,#050505_98%)]" />

      {/* intro curtain */}
      <motion.div
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.1 }}
        style={{ transformOrigin: "top" }}
        className="absolute inset-0 z-30 bg-[#050505]"
      />

      <motion.div style={{ y: textY }} className="relative z-20 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-[#F5B841]" />
          <span className="text-xs md:text-sm tracking-[0.35em] text-[#F5B841]">EVENT PRODUCTION · HYDERABAD</span>
        </motion.div>

        <motion.h1
          data-testid="hero-heading"
          variants={container}
          initial="initial"
          animate="animate"
          className="font-display font-semibold tracking-tighter leading-[0.9] text-5xl sm:text-7xl md:text-8xl lg:text-[8.5rem] text-white"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineUp} className={`block ${i === 1 ? "text-[#F5B841] italic" : ""}`}>
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="max-w-md text-base md:text-lg text-white/65 font-light"
          >
            LED video walls, stage &amp; truss, cinematic lighting and concert-grade sound — engineered end-to-end for
            annual days, concerts, political meetings and celebrations.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.6 }}
            className="flex items-center gap-4"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={() => scrollTo("quote")}
              className="group inline-flex items-center gap-2 bg-[#F5B841] text-black font-semibold px-7 py-4 hover:bg-white transition-colors"
            >
              Request a Quote
              <ArrowDownRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollTo("services")}
              className="text-white/80 border border-white/20 px-7 py-4 hover:border-[#F5B841] hover:text-[#F5B841] transition-colors"
            >
              Explore Services
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
